/**
 * Per-player tuning overlay (06 §1, 12 §8.1).
 *
 * The sim never reads a tunable constant directly for a player or disc — it
 * reads `player.tuning[KEY]` / `disc.tuning[KEY]`. `tuning` is BASE ⊕ the
 * overlays of a *list* of power-ups (Phase 1 lists have length ≤ 1). Two
 * overlays touching the same key is an error the resolver settles
 * deterministically: the first overlay wins and the colliding key is
 * reported in `rejected`. The power-up pool itself is data (06 §2) and is
 * not part of this module.
 */
import * as C from './constants.js'

/** Keys from 01 §D–§G and §J that a power-up may override. */
export const TUNING_KEYS = [
  // D
  'PLAYER_RADIUS', 'MOVE_SPEED', 'MOVE_ACCEL', 'MOVE_DECEL', 'SHIELD_MOVE_MULT',
  'DASH_SPEED', 'DASH_TICKS', 'DASH_COST', 'DASH_COOLDOWN_TICKS',
  'STAMINA_MAX', 'STAMINA_REGEN', 'STAMINA_REGEN_DELAY_TICKS', 'EXHAUST_TICKS',
  // E
  'SHIELD_RAISE_COST', 'SHIELD_MIN_RAISE', 'SHIELD_DRAIN', 'SHIELD_PERFECT_TICKS',
  'SHIELD_RADIUS', 'SHIELD_ARC_HALF_DEG', 'SHIELD_DROP_LOCKOUT_TICKS',
  'DEFLECT_PERFECT_SPEED_MULT', 'DEFLECT_PERFECT_REFUND', 'DEFLECT_LATE_SPEED_MULT', 'DEFLECT_LATE_COST',
  // F
  'THROW_WINDUP_TICKS', 'THROW_RECOVERY_TICKS', 'THROW_SHIELD_LOCK_TICKS', 'THROW_ORIGIN_OFFSET',
  'CATCH_RADIUS', 'PICKUP_RADIUS', 'MAX_DISCS_HELD_BASE',
  // G
  'DISC_RADIUS', 'DISC_THROW_SPEED', 'DISC_RANGE', 'DISC_MAX_BOUNCES', 'DISC_BOUNCE_RESTITUTION',
  'DISC_MIN_FLIGHT_SPEED', 'DISC_RETURN_SPEED', 'DISC_RETURN_TURN_RATE',
  'DISC_RETURN_TIMEOUT_TICKS', 'DISC_GROUND_TIMEOUT_TICKS', 'DISC_GROUND_SLIDE_DECEL',
  // J
  'DASH_IFRAME_TICKS', 'DISC_SPEED_CAP_MULT', 'DISC_SHIELD_PIERCE', 'INTERCEPT_ENABLED',
] as const

export type TuningKey = (typeof TUNING_KEYS)[number]
export type Tuning = { [K in TuningKey]: (typeof C)[K] extends boolean ? boolean : number }
export type TuningOverlay = Partial<Tuning>

/** Disc-scoped keys (01 §G + J) copied onto a disc at throw / perfect deflect. */
export const DISC_TUNING_KEYS = [
  'DISC_RADIUS', 'DISC_THROW_SPEED', 'DISC_RANGE', 'DISC_MAX_BOUNCES', 'DISC_BOUNCE_RESTITUTION',
  'DISC_MIN_FLIGHT_SPEED', 'DISC_RETURN_SPEED', 'DISC_RETURN_TURN_RATE',
  'DISC_RETURN_TIMEOUT_TICKS', 'DISC_GROUND_TIMEOUT_TICKS', 'DISC_GROUND_SLIDE_DECEL',
  'DISC_SPEED_CAP_MULT', 'DISC_SHIELD_PIERCE',
] as const
export type DiscTuningKey = (typeof DISC_TUNING_KEYS)[number]
export type DiscTuning = Pick<Tuning, DiscTuningKey>

export const BASE_TUNING: Readonly<Tuning> = Object.freeze(
  Object.fromEntries(TUNING_KEYS.map((k) => [k, C[k]])) as Tuning,
)

export interface ResolvedTuning {
  tuning: Tuning
  /** Keys a later overlay tried to set after an earlier one already had — ignored. */
  rejected: TuningKey[]
}

/** BASE ⊕ knobs ⊕ overlay[0] ⊕ overlay[1] … with disjoint-key enforcement among the
 *  overlays only. `knobs` is the 13 §4 playtest overlay (a room-wide constant change for one
 *  session, e.g. `{ DISC_THROW_SPEED: 27 }`); power-ups layer over it exactly as over BASE. */
export function resolveTuning(overlays: readonly TuningOverlay[], knobs?: TuningOverlay | null): ResolvedTuning {
  const tuning = { ...BASE_TUNING } as Tuning
  if (knobs) for (const k of TUNING_KEYS) if (knobs[k] !== undefined) (tuning as Record<string, number | boolean>)[k] = knobs[k] as number | boolean
  const owned = new Set<TuningKey>()
  const rejected: TuningKey[] = []
  for (const o of overlays) {
    for (const k of TUNING_KEYS) {
      if (!(k in o) || o[k] === undefined) continue
      if (owned.has(k)) { rejected.push(k); continue }
      owned.add(k)
      ;(tuning as Record<string, number | boolean>)[k] = o[k] as number | boolean
    }
  }
  // 06 §2 hard caps: no override may exceed the sim's allocation bounds.
  if (tuning.MAX_DISCS_HELD_BASE > C.MAX_DISCS_HELD_CAP) tuning.MAX_DISCS_HELD_BASE = C.MAX_DISCS_HELD_CAP
  return { tuning, rejected }
}

export function discTuningFrom(t: Tuning): DiscTuning {
  const out = {} as Record<string, number | boolean>
  for (const k of DISC_TUNING_KEYS) out[k] = t[k]
  return out as DiscTuning
}

/** Absolute disc speed ceiling (06 §2 invariant): base throw speed × cap mult. */
export const discSpeedCap = (t: { DISC_SPEED_CAP_MULT: number }): number => C.DISC_THROW_SPEED * t.DISC_SPEED_CAP_MULT
